import React, { useState, useEffect, useContext } from "react";
import { db, functions } from '../firebase/config';
import { collection, getDocs, doc, deleteDoc, updateDoc, addDoc, Timestamp } from "firebase/firestore";
import { AuthContext } from '../context/AuthContext';
import { Minus, Plus, ShoppingBag, Trash2 } from 'lucide-react';
import Footer from "../components/Footer";
import FeaturedProducts from "../components/FeaturedProducts";
import './MyBag.css';
import { httpsCallable } from "firebase/functions";

export default function MyBag() {
  const { user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);

  // New state for delivery address
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');

  // State to control checkout form visibility
  const [isCheckoutFormVisible, setIsCheckoutFormVisible] = useState(false);
  const [isDeliveryFormFilled, setIsDeliveryFormFilled] = useState(false);

  // Ensure Paystack public key is correctly set
  const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || '';

  // Calculate total amount in kobo (Paystack requires amount in kobo)
  const calculateTotalAmount = () => {
    return Math.round(
      items.reduce((total, item) => total + (item.price * item.quantity), 0) * 100
    );
  };

  useEffect(() => {
    const fetchBagItems = async () => {
      if (!user) return;
      try {
        const itemsCollection = collection(db, 'myBag', user.uid, 'items');
        const itemsSnapshot = await getDocs(itemsCollection);
        const itemsList = itemsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setItems(itemsList);

        // Pre-fill customer details if available
        if (user) {
          setName(user.displayName || '');
          setEmail(user.email || '');
        }
      } catch (error) {
        console.error("Error fetching bag items:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBagItems();
  }, [user]);

  const removeFromBag = async (itemId) => {
    if (!user) return;
    try {
      const itemRef = doc(db, 'myBag', user.uid, 'items', itemId);
      await deleteDoc(itemRef);
      setItems(items.filter(item => item.id !== itemId));
    } catch (error) {
      console.error("Error removing item:", error);
    }
  };

  const updateQuantity = async (itemId, newQuantity) => {
    if (!user) return;
    try {
      if (newQuantity < 1) {
        await removeFromBag(itemId);
      } else {
        const itemRef = doc(db, 'myBag', user.uid, 'items', itemId);
        await updateDoc(itemRef, { quantity: newQuantity });
        setItems(items.map(item => 
          item.id === itemId ? { ...item, quantity: newQuantity } : item
        ));
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const verifyTransaction = httpsCallable(functions, "verifyTransaction");

  const createOrder = async (reference) => {
      if (!user) return;
  
      try {
          const verificationResponse = await verifyTransaction({ reference });
  
          if (verificationResponse.data.success) {
              const orderData = {
                  userId: user.uid,
                  customerName: name,
                  customerEmail: email,
                  customerPhone: phone,
                  paymentReference: reference,
                  items: items.map(item => ({
                      name: item.name,
                      price: item.price,
                      quantity: item.quantity,
                      size: item.size || null,
                      color: item.color || null,
                  })),
                  totalAmount: items.reduce((total, item) => total + (item.price * item.quantity), 0),
                  createdAt: Timestamp.now(),
                  status: 'paid',
                  deliveryAddress: {
                      street,
                      city,
                      state,
                      country,
                      postalCode,
                  },
              };
  
              await addDoc(collection(db, "orders"), orderData);
              console.log("Order created successfully.");
  
              // Clear the bag after successful order
              const bagItemsCollection = collection(db, 'myBag', user.uid, 'items');
              const snapshot = await getDocs(bagItemsCollection);
              snapshot.docs.forEach(async (document) => {
                  await deleteDoc(doc(db, 'myBag', user.uid, 'items', document.id));
              });
  
              setItems([]);
              setIsCheckoutFormVisible(false);
              setIsDeliveryFormFilled(false);
          } else {
              console.error("Transaction verification failed.");
          }
      } catch (error) {
          console.error("Error verifying transaction or creating order:", error);
      }
  };

  // Modify the componentProps to explicitly ensure key is a string
  const handlePayment = () => {
    const handler = window.PaystackPop.setup({
      key: publicKey, // Replace with your Paystack public key
      email,
      amount: calculateTotalAmount(),
      currency: 'NGN',
      metadata: { name, phone },
      callback: (response) => {
        createOrder(response.reference);
        alert(`Payment successful! Reference: ${response.reference}`);
      },
      onClose: () => {
        alert('Payment cancelled.');
      },
    });

    handler.openIframe();
  };

  // Validate delivery form
  const validateDeliveryForm = () => {
    return street && city && state && country && postalCode;
  };

  // Handle checkout button click
  const handleCheckoutClick = () => {
    setIsCheckoutFormVisible(true);
  };

  // Handle proceed to payment
  const handleProceedToPayment = () => {
    if (validateDeliveryForm()) {
      setIsDeliveryFormFilled(true);
    } else {
      alert('Please fill in all delivery address fields');
    }
  };

    if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-overlay">
        <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="my-bag-container">
              <div className="my-bag-content">
                <div className="mbheader">
                  <h1>Shopping Bag</h1>
                  <div className="bag-summary">
                    <ShoppingBag className="shopping-bag-icon" />
                    <span>{items.reduce((total, item) => total + item.quantity, 0)} items</span>
                  </div>
                </div>

                {items.length === 0 ? (
                  <div className="empty-bag">
                    <ShoppingBag className="empty-bag-icon" />
                    <h2>Your bag is empty</h2>
                    <p>Looks like you haven't added any items yet.</p>
                  </div>
                ) : (
                  <div className="bag-items">
                    <ul className="item-list">
                      {items.map((item) => (
                        <li key={item.id} className="item">
                          <div className="item-info">
                            <img 
                              src={item.images[0]} 
                              alt={item.name}
                              className="item-image"
                            />
                            <div className="item-details">
                              <h3>{item.name}</h3>
                              <p>&#8358;{item.price.toFixed(2)}</p>
                            </div>
                            <div className="quantity-controls">
                              <button 
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                className="quantity-btn"
                              >
                                <Minus />
                              </button>
                              <span>{item.quantity}</span>
                              <button 
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                className="quantity-btn"
                              >
                                <Plus />
                              </button>
                            </div>
                            <button
                              onClick={() => removeFromBag(item.id)}
                              className="remove-btn"
                            >
                              <Trash2 />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div className="checkout-section">
                      <div className="total">
                        <p>Total</p>
                        <p>&#8358;{items.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2)}</p>
                      </div>

                      {!isCheckoutFormVisible && (
                        <button 
                          onClick={handleCheckoutClick} 
                          className="proceed-to-checkout-btn"
                        >
                          Proceed to Checkout
                        </button>
                      )}

                      {isCheckoutFormVisible && !isDeliveryFormFilled && (
                        <div className="checkout-form">
                          {/* Customer Details */}
                          <div className="customer-details">
                            <div className="checkout-field">
                              <label>Name</label>
                              <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Enter your full name"
                                required
                              />
                            </div>
                            <div className="checkout-field">
                              <label>Email</label>
                              <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                required
                              />
                            </div>
                            <div className="checkout-field">
                              <label>Phone</label>
                              <input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="Enter your phone number"
                                required
                              />
                            </div>

                          {/* Delivery Address Fields */}
                          <div className="delivery-address">
                            <h3>Delivery Address</h3>
                            <div className="checkout-field">
                              <label>Street Address</label>
                              <input
                                type="text"
                                value={street}
                                onChange={(e) => setStreet(e.target.value)}
                                placeholder="Enter street address"
                                required
                              />
                            </div>
                            <div className="checkout-field">
                              <label>City</label>
                              <input
                                type="text"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                placeholder="Enter city"
                                required
                              />
                            </div>
                            <div className="checkout-field">
                              <label>State</label>
                              <input
                                type="text"
                                value={state}
                                onChange={(e) => setState(e.target.value)}
                                placeholder="Enter state"
                                required
                              />
                            </div>
                            <div className="checkout-field">
                              <label>Country</label>
                              <input
                                type="text"
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                placeholder="Enter country"
                                required
                              />
                            </div>
                            <div className="checkout-field">
                              <label>Postal Code</label>
                              <input
                                type="text"
                                value={postalCode}
                                onChange={(e) => setPostalCode(e.target.value)}
                                placeholder="Enter postal code"
                                required
                              />
                            </div>

                            <button 
                              onClick={handleProceedToPayment} 
                              className="proceed-to-payment-btn"
                            >
                              Proceed to Payment
                            </button>
                          </div>
                        </div>
                        </div>
                      )}

                      {isCheckoutFormVisible && isDeliveryFormFilled && (
                        <div className="payment-section">
                          <button 
                            onClick={handlePayment} 
                            className="checkout-btn"
                          >
                            Checkout with Paystack
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="featured-products-section">
                  <FeaturedProducts />
                </div>
              </div>
              </div>
      <Footer />
    </div>
  );
}